<script setup lang="ts">
import type { ExchangeItem as ExchangeItemType } from '@/types/shared/exchange'
import { EXCHANGE_ITEM_COST_TYPE } from '@/types/shared/exchange'
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import ExchangeItem from '@/components/exchange/ExchangeItem.vue'
import { twMerge } from 'tailwind-merge'
import { computed } from 'vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { usePlayer } from '@/composables/game/player'
import { rewardImageMap, getExchangeRewardDisplay } from '@/composables/game/exchange-rewards'

const props = defineProps<{
  show: boolean
  item: ExchangeItemType
  itemId: string
  isLoading?: boolean
}>()

const emit = defineEmits<{
  (e: 'hide'): void
  (e: 'confirm'): void
}>()

const { goldenCoinValue } = usePlayer()

const itemRewardDisplay = computed(() => {
  return getExchangeRewardDisplay(props.item.reward, goldenCoinValue.value)
})

const costImage = computed(() => {
  if (!props.item.cost || props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.FREE || props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.STAR) {
    return undefined
  }

  if (props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY && props.item.cost.currencyId) {
    return rewardImageMap.value[props.item.cost.currencyId]
  }
  return undefined
})

function getCostLabel() {
  if (!props.item.cost || props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.FREE) {
    return 'Unlock for free'
  }
  if (props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY) {
    return `Buy with `
  }
  return 'Unlock'
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="pt-6"
    bg-fade-class="!z-[804]"
    wrapper-class="!z-[804]"
    @hide="$emit('hide')"
  >
    <div class="relative w-full pt-7">
      <StarsBalancePill
        class="absolute left-0 top-0"
        :style="'dark'"
      />
      <ExchangeItem
        :class="twMerge('mx-auto scale-[1.15] pointer-events-none')"
        :item-id="itemId"
        :item="item"
        hide-cost
      />

      <div class="mt-3 flex w-full flex-col items-center">
        <div
          v-if="itemRewardDisplay"
          class="text-[#FFFEFD] text-[24px] leading-8 font-bold font-pilat"
        >
          {{ itemRewardDisplay.amount }} {{ itemRewardDisplay.title }}
        </div>

        <div class="mt-6 flex w-full flex-col gap-y-3">
          <BaseButton
            class="w-full"
            :loading="props.isLoading"
            @click="emit('confirm')"
          >
            <span v-if="!props.isLoading">
              {{ getCostLabel() }}
              <img
                v-if="costImage"
                :src="costImage"
                class="inline size-[20px] align-middle"
                alt="Cost Icon"
              >{{ item.cost && item.cost.type !== EXCHANGE_ITEM_COST_TYPE.FREE ? ` ${formatNumberFullTruncate(item.cost.amount)}` : '' }}
            </span>
            <span v-else>
              Processing...
            </span>
          </BaseButton>
        </div>
      </div>
    </div>
  </TheBottomSheet>
</template>
